import React from 'react';
import SEO from '../components/SEO';
import { seoData } from '../data/seo';
import { Link } from 'react-router-dom';
import { Layout, RefreshCw, Share2, TrendingUp, Check, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const Services = () => {
  const iconMap = {
    Layout: Layout,
    RefreshCw: RefreshCw,
    Share2: Share2,
    TrendingUp: TrendingUp
  };

  return (
    <div className="min-h-screen">
      <SEO {...seoData.services} />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Our <span className="text-cyan-500">Services</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Comprehensive digital solutions tailored to your business needs
          </p>
        </div>
      </section>

      {/* Services Details */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={service.id}
                  id={service.slug}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center scroll-mt-24 ${
                    !isEven ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <Card className="p-8 lg:p-12 bg-gradient-to-br from-cyan-50 to-white rounded-lg">
                      <div className="w-16 h-16 bg-cyan-500 rounded-lg flex items-center justify-center mb-6">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {service.title}
                      </h2>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
                    </Card>
                  </div>

                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                      What You Get
                    </h3>
                    <ul className="space-y-4">
                      {service.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                            <Check className="w-4 h-4 text-cyan-500" />
                          </div>
                          <span className="text-lg text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Custom Solutions for Every Budget
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Every project is unique. We tailor our services to fit your specific needs and budget. 
            Let's discuss your goals and create a custom package that works for you.
          </p>
          <Link to="/contact">
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-8 py-6 text-lg">
              Get a Custom Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, transparent, and effective
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Card className="p-6 text-center rounded-lg">
              <div className="w-12 h-12 bg-cyan-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Discovery</h3>
              <p className="text-gray-600">
                We learn about your business, goals, and target audience.
              </p>
            </Card>

            <Card className="p-6 text-center rounded-lg">
              <div className="w-12 h-12 bg-cyan-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategy</h3>
              <p className="text-gray-600">
                We create a customized plan tailored to your needs.
              </p>
            </Card>

            <Card className="p-6 text-center rounded-lg">
              <div className="w-12 h-12 bg-cyan-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Execution</h3>
              <p className="text-gray-600">
                We bring your vision to life with precision and creativity.
              </p>
            </Card>

            <Card className="p-6 text-center rounded-lg">
              <div className="w-12 h-12 bg-cyan-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                4
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Launch</h3>
              <p className="text-gray-600">
                We deliver results and provide ongoing support.
              </p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
